import { CalendarCheck } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, newIdempotencyKey } from "../api";
import { defaultAppointmentEnd, localDateTimeInputValue } from "../appointment-time";
import { useAuth } from "../auth";
import { PageBackLink, PageHeader, StateView } from "../components";
import { useApiData } from "../hooks";
import { serviceLocationAddress } from "../location-label";
import { workOrderNetAmount } from "../money";
import type { Appointment, CursorPage, Customer, Employee, QuoteDetail, ServiceLocation, ServiceType, WorkOrder } from "../types";

const normalizeMoney = (raw: string) => {
  const clean = raw.trim().replace(/\s/g, "").replace("R$", "").replace(/\.(?=\d{3}(?:\D|$))/g, "").replace(",", ".");
  if (!/^\d+(?:\.\d{0,2})?$/.test(clean)) return null;
  const [whole, fraction = ""] = clean.split(".");
  return `${whole}.${fraction.padEnd(2, "0")}`;
};

export function NewAppointmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quoteId");
  const workOrderId = searchParams.get("workOrderId");
  const initializedFromQuote = useRef(false);
  const { user } = useAuth();
  const startDefault = useMemo(() => {
    const date = new Date(); date.setMinutes(0, 0, 0); date.setHours(date.getHours() + 1); return date;
  }, []);
  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [serviceTypeId, setServiceTypeId] = useState("");
  const [assignee, setAssignee] = useState("");
  const [startsAt, setStartsAt] = useState(localDateTimeInputValue(startDefault));
  const [endsAt, setEndsAt] = useState(defaultAppointmentEnd(localDateTimeInputValue(startDefault)));
  const [agreedValue, setAgreedValue] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const customers = useApiData<CursorPage<Customer>>("/customers?pageSize=100&status=active");
  const locations = useApiData<ServiceLocation[]>(customerId ? `/customers/${customerId}/locations` : null);
  const services = useApiData<CursorPage<ServiceType>>("/service-types?pageSize=100&active=true");
  const employees = useApiData<Employee[]>("/employees?active=true");
  const quote = useApiData<QuoteDetail>(quoteId ? `/quotes/${quoteId}` : null);
  const workOrder = useApiData<WorkOrder>(workOrderId ? `/work-orders/${workOrderId}` : null);

  useEffect(() => {
    if (!quote.data || initializedFromQuote.current) return;
    const firstItem = quote.data.items[0];
    setCustomerId(quote.data.customerId); setLocationId(quote.data.locationId);
    if (firstItem) setServiceTypeId(firstItem.serviceTypeId);
    setAgreedValue(quote.data.total.replace(".", ","));
    const method = quote.data.paymentTerms.method;
    if (typeof method === "string") setPaymentMethod(method);
    initializedFromQuote.current = true;
  }, [quote.data]);

  useEffect(() => {
    if (!workOrder.data || initializedFromQuote.current) return;
    const firstItem = workOrder.data.items?.[0];
    const primaryVisit = workOrder.data.appointments?.find(({ status }) => !["rescheduled", "cancelled", "not_performed"].includes(status));
    setCustomerId(workOrder.data.customerId);
    setLocationId(workOrder.data.locationId);
    if (primaryVisit) setServiceTypeId(primaryVisit.serviceTypeId);
    else if (firstItem) setServiceTypeId(firstItem.serviceTypeId);
    setAgreedValue(workOrderNetAmount(workOrder.data).replace(".", ","));
    if (workOrder.data.employees?.[0]?.employeeId) setAssignee(workOrder.data.employees[0].employeeId);
    initializedFromQuote.current = true;
  }, [workOrder.data]);

  const chooseService = (id: string) => {
    setServiceTypeId(id);
    const service = services.data?.data.find((item) => item.id === id);
    if (service) {
      setAgreedValue(service.defaultPrice.replace(".", ","));
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    const money = normalizeMoney(agreedValue);
    if (!money) return setError("Informe o valor com até duas casas decimais.");
    const branchId = quoteId ? quote.data?.branchId : workOrderId ? workOrder.data?.branchId : user?.branchId;
    if (!branchId) return setError("Filial não identificada para este agendamento.");
    if (!employees.data?.length) return setError("Cadastre um funcionário antes de criar o agendamento.");
    if (!assignee) return setError("Selecione o funcionário responsável pelo serviço.");
    if (new Date(endsAt) <= new Date(startsAt)) return setError("O término deve ser posterior ao início.");
    const assignments = [{ type: "employee" as const, id: assignee, quantity: 1 }];
    const payload = {
      customerId, locationId, serviceTypeId, ...(quoteId ? { quoteId } : {}),
      branchId,
      startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString(),
      timezone: "America/Sao_Paulo", travelBeforeMinutes: 0, priority,
      agreedValue: money, expectedPaymentMethod: paymentMethod, origin: quoteId ? "quote" : "web",
      notes: notes.trim() || undefined,
      assignments,
    };
    setBusy(true);
    try {
      const conflicts = await api<{ hasBlockingConflicts: boolean; conflicts: Array<{ explanation: string }> }>("/appointments/conflicts", {
        method: "POST", body: JSON.stringify(payload),
      });
      if (conflicts.hasBlockingConflicts) {
        setError(conflicts.conflicts.map(({ explanation }) => explanation).join(" ") || "Há conflito de agenda.");
        return;
      }
      if (workOrderId) {
        await api<Appointment>(`/work-orders/${workOrderId}/appointments`, {
          method: "POST",
          headers: { "Idempotency-Key": newIdempotencyKey() },
          body: JSON.stringify({
            startsAt: payload.startsAt, endsAt: payload.endsAt,
            timezone: payload.timezone, travelBeforeMinutes: payload.travelBeforeMinutes,
            priority, notes: notes.trim() || undefined, assignments: payload.assignments,
          }),
        });
        navigate(`/ordens/${workOrderId}`, { replace: true });
        return;
      }
      let appointment: Appointment;
      if (quoteId && quote.data) {
        appointment = await api<Appointment>(`/quotes/${quoteId}/convert`, {
          method: "POST",
          headers: { "Idempotency-Key": newIdempotencyKey(), "If-Match": `"${quote.data.version}"` },
          body: JSON.stringify({
            serviceTypeId, startsAt: payload.startsAt, endsAt: payload.endsAt,
            timezone: payload.timezone, travelBeforeMinutes: payload.travelBeforeMinutes,
            priority, expectedPaymentMethod: paymentMethod, notes: notes.trim() || undefined,
            assignments: payload.assignments,
          }),
        });
      } else {
        appointment = await api<Appointment>("/appointments", {
          method: "POST", headers: { "Idempotency-Key": newIdempotencyKey() }, body: JSON.stringify(payload),
        });
      }
      const confirmation = await api<{ appointment: Appointment; workOrder: WorkOrder }>(`/appointments/${appointment.id}/confirm`, {
        method: "POST",
        headers: { "If-Match": `"${appointment.version}"`, "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({}),
      });
      navigate(`/ordens/${confirmation.workOrder.id}`, { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível criar o agendamento.");
    } finally { setBusy(false); }
  };

  const loading = customers.loading || services.loading || employees.loading || quote.loading || workOrder.loading;
  const loadError = customers.error || services.error || employees.error || quote.error || workOrder.error;
  const lockedToOrder = Boolean(quoteId || workOrderId);

  return <>
    <section className="page-intro"><div className="app-container">
      <PageBackLink to={workOrderId ? `/ordens/${workOrderId}` : quoteId ? "/orcamentos" : "/agenda"} context={workOrderId ? "Ordem de serviço" : quoteId ? "Orçamentos" : "Agenda"} />
      <PageHeader title={workOrderId ? "Adicionar dia de execução" : "Novo agendamento"} />
    </div></section>
    <section className="app-section compact"><div className="app-container appointment-form-shell">
      <StateView loading={loading} error={loadError}>
        <form className="appointment-form" onSubmit={submit}>
          <section className="form-section">
            <h2>Cliente e serviço</h2>
            <div className="form-grid">
              <label>Cliente<select value={customerId} onChange={(event) => { setCustomerId(event.target.value); setLocationId(""); }} disabled={lockedToOrder} required><option value="">Selecione</option>{customers.data?.data.map((customer) => <option key={customer.id} value={customer.id}>{customer.legalName}</option>)}</select></label>
              <label>Endereço do serviço<select value={locationId} onChange={(event) => setLocationId(event.target.value)} disabled={lockedToOrder || !customerId || locations.loading} required><option value="">Selecione</option>{locations.data?.map((location) => <option key={location.id} value={location.id}>{serviceLocationAddress(location)}</option>)}</select></label>
              {quoteId && quote.data
                ? <div className="appointment-quote-services">
                  <span>Serviços do orçamento</span>
                  <div>{quote.data.items.map((item) => <p key={item.id}><strong>{item.serviceName}</strong><small>{Number(item.quantity).toLocaleString("pt-BR", { maximumFractionDigits: 3 })} {item.unit}</small></p>)}</div>
                  </div>
                : workOrderId && workOrder.data
                  ? <div className="appointment-quote-services">
                    <span>Serviços da OS {workOrder.data.number}</span>
                    <div>{workOrder.data.items?.map((item) => <p key={item.serviceTypeId}><strong>{item.name}</strong><small>{Number(item.quantity).toLocaleString("pt-BR", { maximumFractionDigits: 3 })} {item.unit}</small></p>)}</div>
                  </div>
                : <label>Serviço<select value={serviceTypeId} onChange={(event) => chooseService(event.target.value)} required><option value="">Selecione</option>{services.data?.data.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}</select></label>}
              <label className="appointment-assignee-field">Responsável
                <select value={assignee} onChange={(event) => setAssignee(event.target.value)} required disabled={!employees.data?.length}>
                  <option value="">{employees.data?.length ? "Selecione o funcionário" : "Nenhum funcionário cadastrado"}</option>
                  {employees.data?.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
                </select>
                {!employees.data?.length && <small className="appointment-assignee-empty">É necessário <Link to="/equipe">cadastrar um funcionário</Link> antes de agendar.</small>}
              </label>
            </div>
          </section>

          <section className="form-section">
            <h2>{workOrderId ? "Novo dia de execução" : "Data e valor"}</h2>
            <div className="form-grid">
              <label>Início<input type="datetime-local" value={startsAt} onChange={(event) => { const nextStart = event.target.value; setStartsAt(nextStart); const nextEnd = defaultAppointmentEnd(nextStart); if (nextEnd) setEndsAt(nextEnd); }} required /></label>
              <label>Término<input type="datetime-local" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} required /></label>
              {!workOrderId && <label>Valor da OS<input inputMode="decimal" value={agreedValue} onChange={(event) => setAgreedValue(event.target.value)} placeholder="0,00" required readOnly={lockedToOrder} /></label>}
              {!workOrderId && <label>Pagamento<select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)}><option value="pix">Pix</option><option value="cash">Dinheiro</option><option value="credit_card">Cartão de crédito</option><option value="debit_card">Cartão de débito</option><option value="boleto">Boleto</option><option value="bank_transfer">Transferência</option><option value="other">Outro</option></select></label>}
              <label>Prioridade<select value={priority} onChange={(event) => setPriority(event.target.value)}><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option><option value="low">Baixa</option></select></label>
              <label className="field-span-2">Observações<textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={4000} /></label>
            </div>
          </section>

          {error && <div className="form-error" role="alert">{error}</div>}
          <footer className="form-actions"><Link className="site-button site-button-outline" to={workOrderId ? `/ordens/${workOrderId}` : "/agenda"}>Cancelar</Link><button className="site-button site-button-red" disabled={busy || !employees.data?.length}><CalendarCheck size={17} />{busy ? "Verificando…" : workOrderId ? "Adicionar dia" : "Criar agendamento"}</button></footer>
        </form>
      </StateView>
    </div></section>
  </>;
}
